import React, { useState, useEffect } from 'react';
import {
  BarChart2,
  Activity,
  CheckCircle,
  XCircle,
  TrendingUp,
  RefreshCw,
  Terminal,
  Clock,
  HardDrive,
  Ban,
  Zap
} from 'lucide-react';
import './Sections.css';



function formatDuration(seconds) {
  if (!seconds || seconds < 1) return seconds + 's';

  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);

  if (h > 0) {
    return `${h}h ${m}m ${s}s`;
  } else if (m > 0) {
    return `${m}m ${s}s`;
  } else {
    return `${s}s`;
  }
}

function MonitorSection({ apiUrl, jobs, onRefresh }) {
  const [stats, setStats] = useState({
    totalJobs: 0,
    runningJobs: 0,
    completedJobs: 0,
    failedJobs: 0,
    cancelledJobs: 0,
    successRate: 0,
    totalVolume: 0
  });

  const [throughputData, setThroughputData] = useState([]);
  const [selectedJobId, setSelectedJobId] = useState(null);
  const [selectedJob, setSelectedJob] = useState(null);
  const [logs, setLogs] = useState([]);

  useEffect(() => {
    if (selectedJobId) {
      const job = jobs.find(j => j.id.toString() === selectedJobId.toString());
      setSelectedJob(job);
    } else {
      setSelectedJob(null);
    }
  }, [selectedJobId, jobs]);

  useEffect(() => {
    const calculateStats = () => {
      const total = jobs.length;
      const running = jobs.filter(job => job.status === 'running').length;
      const completed = jobs.filter(job => job.status === 'completed').length;
      const failed = jobs.filter(job => job.status === 'failed').length;
      const cancelled = jobs.filter(job => job.status === 'cancelled').length;
      const successRate = total > 0 ? Math.round((completed / (completed + failed + cancelled)) * 100) : 0;
      const totalVolume = jobs.reduce((sum, job) => sum + (parseInt(job.total_bytes) || 0), 0);

      setStats({
        totalJobs: total,
        runningJobs: running,
        completedJobs: completed,
        failedJobs: failed,
        cancelledJobs: cancelled,
        successRate,
        totalVolume
      });

      if (running > 0 && !selectedJobId) {
        const activeJob = jobs.find(j => j.status === 'running');
        setSelectedJobId(activeJob.id);
      }
    };

    calculateStats();
  }, [jobs, selectedJobId]);

  useEffect(() => {
    const fetchThroughput = async () => {
      try {
        const url = selectedJobId
          ? `${apiUrl}/api/migrations/stats/throughput?jobId=${selectedJobId}`
          : `${apiUrl}/api/migrations/stats/throughput`;
        const response = await fetch(url);
        const data = await response.json();
        if (data.success) setThroughputData(data.stats);
      } catch (err) {
        console.error('Failed to fetch stats:', err);
      }
    };
    fetchThroughput();
    const interval = setInterval(fetchThroughput, 5000);
    return () => clearInterval(interval);
  }, [apiUrl, selectedJobId]);

  useEffect(() => {
    let interval;
    if (selectedJobId) {
      const fetchLogs = async () => {
        try {
          const response = await fetch(`${apiUrl}/api/migrations/${selectedJobId}/logs`);
          const data = await response.json();
          if (data.success) setLogs(data.logs);
        } catch (err) {
          console.error('Logs fetch failed:', err);
        }
      };
      fetchLogs();
      interval = setInterval(fetchLogs, 3000);
    } else {
      setLogs([]);
    }
    return () => clearInterval(interval);
  }, [selectedJobId, apiUrl]);

  const renderUnifiedChart = () => {
    if (!throughputData || throughputData.length === 0) return null;
    const volumes = throughputData.map(d => parseFloat(d.volume) || 0);
    const maxVal = Math.max(...volumes, 1);
    const height = 180;
    const width = 500;
    const padding = 20;

    const points = throughputData.map((d, i) => {
      let volume = parseFloat(d.volume) || 0;
      if (volume === 0 && d.activeCount > 0) volume = maxVal * 0.005;
      const x = (i / (throughputData.length - 1)) * (width - padding * 2) + padding;
      const y = height - ((volume / maxVal) * (height - padding * 2) + padding);
      return `${x},${y}`;
    }).join(' ');

    return (
      <div className="chart-container unified">
        <div className="chart-canvas-wrapper">
          <svg viewBox={`0 0 ${width} ${height}`} className="throughput-svg">
            <defs>
              <linearGradient id="chartGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="rgba(99, 102, 241, 0.4)" />
                <stop offset="100%" stopColor="transparent" />
              </linearGradient>
              <filter id="glow">
                <feGaussianBlur stdDeviation="2" result="coloredBlur" />
                <feMerge>
                  <feMergeNode in="coloredBlur" />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>
            </defs>

            {/* Area fill */}
            <path d={`M ${width - padding} ${height - padding} L ${padding} ${height - padding} L ${points} L ${width - padding} ${height - padding}`} fill="url(#chartGradient)" opacity="0.6" />

            {/* Tech Grid - Fine lines */}
            {Array.from({ length: 10 }).map((_, i) => (
              <line
                key={`grid-v-${i}`}
                x1={padding + (i * (width - padding * 2) / 10)}
                y1={padding}
                x2={padding + (i * (width - padding * 2) / 10)}
                y2={height - padding}
                stroke="var(--grid-line)"
                strokeWidth="1"
              />
            ))}
            {[0.25, 0.5, 0.75].map(v => (
              <line
                key={v}
                x1={padding}
                y1={height - (padding + v * (height - padding * 2))}
                x2={width - padding}
                y2={height - (padding + v * (height - padding * 2))}
                stroke="var(--grid-line)"
                strokeWidth="1"
                strokeDasharray="4,4"
              />
            ))}

            <polyline points={points} fill="none" stroke="var(--primary)" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" filter="url(#glow)" />

            {/* Axis Labels */}
            <text x={padding} y={height - 5} fontSize="10" fill="var(--text-muted)">
              {new Date(throughputData[0].timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </text>
            <text x={width - padding - 40} y={height - 5} fontSize="10" fill="var(--text-muted)">
              {new Date(throughputData[throughputData.length - 1].timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </text>
            <text x={5} y={height / 2} fontSize="10" fill="var(--text-muted)" transform={`rotate(-90, 10, ${height / 2})`}>
              Speed (MB/s)
            </text>

            {/* Event Markers & Tooltips */}
            {throughputData.map((d, i) => {
              if (d.volume <= 0 && d.success <= 0 && d.activeCount <= 0) return null;

              const volume = parseFloat(d.volume) || 0;
              const x = (i / (throughputData.length - 1)) * (width - padding * 2) + padding;
              const y = height - (((volume > 0 ? volume : maxVal * 0.005) / maxVal) * (height - padding * 2) + padding);

              return (
                <circle
                  key={i}
                  cx={x}
                  cy={y}
                  r={d.success > 0 ? "6" : "4"}
                  fill={d.success > 0 ? "#10b981" : "var(--primary)"}
                  className="chart-point"
                  style={{
                    opacity: d.success > 0 ? 1 : 0.8,
                    filter: d.success > 0 ? 'drop-shadow(0 0 8px #10b981)' : 'none'
                  }}
                >
                  <title>{`${new Date(d.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}: ${d.volume} MB/s speed`}</title>
                </circle>
              );
            })}
          </svg>
        </div>

        <div className="graph-legend compact">
          <div className="legend-item">
            <div className="legend-color speed"></div>
            <span>Data Speed</span>
          </div>
          <div className="legend-item">
            <div className="legend-color events" style={{ width: '8px', height: '8px', borderRadius: '50%' }}></div>
            <span>Table Done</span>
          </div>
          <div className="live-indicator miniature">
            <span className="pulse-dot"></span>
            LIVE
          </div>
        </div>
      </div>
    );
  };

  const logEndRef = React.useRef(null);
  const logContainerRef = React.useRef(null);
  const [autoScroll, setAutoScroll] = useState(false);

  // Initialize/Reset auto-scroll when selection changes
  useEffect(() => {
    if (selectedJobId) {
      const job = jobs.find(j => j.id.toString() === selectedJobId.toString());
      // Only auto-scroll by default for active migrations
      setAutoScroll(job?.status === 'running');
    }
  }, [selectedJobId, jobs]);

  // Handle manual scroll to disable/enable auto-scroll
  const handleScroll = () => {
    if (!logContainerRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = logContainerRef.current;
    const isAtBottom = scrollHeight - scrollTop - clientHeight < 50;
    setAutoScroll(isAtBottom);
  };

  useEffect(() => {
    if (autoScroll && logContainerRef.current) {
      // Use scrollTo on the container instead of scrollIntoView on the element
      // to prevent the entire page/window from jumping
      logContainerRef.current.scrollTo({
        top: logContainerRef.current.scrollHeight,
        behavior: 'smooth'
      });
    }
  }, [logs, autoScroll]);


  return (
    <div className="section-container monitor-view">
      <div className="monitor-bg-glow glow-top-right"></div>
      <div className="monitor-bg-glow glow-bottom-left"></div>

      <div className="section-header">
        <h2>System Monitor</h2>
        <button className="btn-secondary" onClick={onRefresh}>
          <RefreshCw size={16} />
          Refresh
        </button>
      </div>

      {/* Statistics Cards */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon total">
            <BarChart2 size={32} />
          </div>
          <div className="stat-info">
            <div className="stat-value">{stats.totalJobs}</div>
            <div className="stat-label">Total Jobs</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon running">
            <Zap size={32} />
          </div>
          <div className="stat-info">
            <div className="stat-value">{stats.runningJobs}</div>
            <div className="stat-label">Running</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon completed">
            <CheckCircle size={32} />
          </div>
          <div className="stat-info">
            <div className="stat-value">{stats.completedJobs}</div>
            <div className="stat-label">Completed</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon failed">
            <XCircle size={32} />
          </div>
          <div className="stat-info">
            <div className="stat-value">{stats.failedJobs}</div>
            <div className="stat-label">Failed</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon cancelled">
            <Ban size={32} />
          </div>
          <div className="stat-info">
            <div className="stat-value">{stats.cancelledJobs}</div>
            <div className="stat-label">Cancelled</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon success">
            <TrendingUp size={32} />
          </div>
          <div className="stat-info">
            <div className="stat-value">{stats.successRate}%</div>
            <div className="stat-label">Success Rate</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon data">
            <HardDrive size={32} />
          </div>
          <div className="stat-info">
            <div className="stat-value">
              {stats.totalVolume > 1024 * 1024 * 1024
                ? (stats.totalVolume / (1024 * 1024 * 1024)).toFixed(2) + ' GB'
                : (stats.totalVolume / (1024 * 1024)).toFixed(1) + ' MB'}
            </div>
            <div className="stat-label">Total Data</div>
          </div>
        </div>
      </div>

      <div className="monitor-details-grid">
        {/* Real-time Logs section */}
        <div className="monitor-card logs-section">
          <div className="monitor-card-header">
            <div className="header-title">
              <Terminal size={20} className="header-icon" />
              <div className="title-group">
                <h3>Historical & Live Logs</h3>
                <span className="subtitle">Activity stream for current or past migrations</span>
              </div>
            </div>
            <div className="header-actions">
              <select
                value={selectedJobId || ''}
                onChange={(e) => setSelectedJobId(e.target.value)}
                className="job-selector"
              >
                <option value="">Select Migration Job</option>
                {jobs.map(job => (
                  <option key={job.id} value={job.id}>
                    {job.name} ({job.status})
                  </option>
                ))}
              </select>
            </div>
          </div>
          {selectedJob && (
            <div className="job-summary-banner">
              <div className="summary-item">
                <div className="summary-label-group">
                  <Activity size={12} className="summary-icon" />
                  <span className="summary-label">Status</span>
                </div>
                <span className={`summary-val status-${selectedJob.status.toUpperCase()}`}>
                  {selectedJob.status.toUpperCase()}
                </span>
              </div>
              <div className="summary-item">
                <div className="summary-label-group">
                  <Clock size={12} className="summary-icon" />
                  <span className="summary-label">Duration</span>
                </div>
                <span className="summary-val">
                  {selectedJob.started_at && selectedJob.completed_at
                    ? formatDuration(Math.floor((new Date(selectedJob.completed_at) - new Date(selectedJob.started_at)) / 1000))
                    : (selectedJob.status === 'running' ? 'In Progress' : 'N/A')}
                </span>
              </div>
              <div className="summary-item">
                <div className="summary-label-group">
                  <Terminal size={12} className="summary-icon" />
                  <span className="summary-label">Started At</span>
                </div>
                <span className="summary-val">
                  {selectedJob.started_at ? new Date(selectedJob.started_at).toLocaleTimeString() : 'N/A'}
                </span>
              </div>
              <div className="summary-item">
                <div className="summary-label-group">
                  <HardDrive size={12} className="summary-icon" />
                  <span className="summary-label">Data Migrated</span>
                </div>
                <span className="summary-val highlight">
                  {(() => {
                    let bytes = parseInt(selectedJob.total_bytes) || 0;

                    // Fallback: Scan logs for row counts if bytes is 0
                    if (bytes === 0 && logs && logs.length > 0) {
                      const rowCountLogs = logs.filter(l => l.message && l.message.includes('has') && l.message.includes('rows'));
                      rowCountLogs.forEach(log => {
                        const match = log.message.match(/has\s+(\d+)\s+rows/);
                        if (match && match[1]) {
                          bytes += parseInt(match[1]) * 250;
                        }
                      });
                    }

                    const mb = bytes / (1024 * 1024);
                    if (mb === 0) return '0 Bytes';
                    if (mb < 0.01) return mb.toFixed(6) + ' MB';
                    return mb.toFixed(2) + ' MB';
                  })()}
                </span>
              </div>
            </div>
          )}
          <div
            className="log-viewer"
            ref={logContainerRef}
            onScroll={handleScroll}
          >
            {/* {!autoScroll && logs.length > 0 && (
              <button
                className="btn-resume-scroll"
                onClick={() => setAutoScroll(true)}
              >
                <RefreshCw size={12} />
                Resume Auto-scroll
              </button>
            )} */}
            {logs.length === 0 ? (
              <div className="empty-logs">
                <Clock size={48} className="empty-icon" />
                <h3>No Detailed Logs</h3>
                {selectedJob ? (
                  <p>Detailed event logs are not available for this legacy migration. Summary metrics are shown above.</p>
                ) : (
                  <p>Select a migration job from the dropdown above to view its activity history.</p>
                )}
              </div>
            ) : (
              logs.map((log, idx) => (
                <div key={idx} className={`log-entry level-${log.level}`}>
                  <span className="log-time">{new Date(log.created_at).toLocaleTimeString()}:</span>
                  <span className={`log-badge badge-${log.level}`}>{log.level.toUpperCase()}</span>
                  <span className="log-msg">
                    {log.message}
                    {log.details && <span className="log-details-hint" title={JSON.stringify(log.details)}>[View Details]</span>}
                  </span>
                </div>
              ))
            )}
            <div ref={logEndRef} />
          </div>
        </div>

        {/* Charts section */}
        <div className="monitor-card charts-section">
          <div className="monitor-card-header">
            <div className="header-title">
              <Activity size={20} className="header-icon" />
              <div className="title-group">
                <h3>{selectedJob ? 'Live Migration Speed' : 'System-wide Data Speed'}</h3>
                <span className="subtitle">
                  {selectedJob
                    ? "Real-time tracking of data moving between databases for this job."
                    : "Live combined speed of all active database migration activities."}
                </span>
              </div>
            </div>
          </div>
          <div className="charts-content">
            <div className="chart-wrapper">
              {renderUnifiedChart()}
              {!throughputData.length && (
                <div className="empty-chart">
                  <Activity size={32} className="pulse" />
                  <p>Ready to monitor... Start a migration to see live speed logs.</p>
                </div>
              )}
            </div>
            <div className="mini-stats-row premium">
              {/* Network Performance Stat */}
              <div className="mini-stat performance-card premium">
                <div className="stat-glow-bg"></div>
                <div className="mini-label-group">
                  <span className="mini-label">Network Performance</span>
                  <div className="stat-icon-halo performance">
                    <TrendingUp size={16} className="stat-mono-icon" />
                  </div>
                </div>
                <div className="stat-main-content">
                  <div className="mini-val-row">
                    <span className="mini-val dashboard-font highlight">
                      {selectedJob ? (
                        (() => {
                          // 1. Try Peak Speed
                          const peakMBs = throughputData.length > 0
                            ? Math.max(...throughputData.map(d => parseFloat(d.volume) || 0))
                            : 0;

                          if (peakMBs > 0) {
                            const peakMbps = peakMBs * 8;
                            return peakMbps < 0.1 ? peakMbps.toFixed(4) : peakMbps.toFixed(1);
                          }

                          // 2. Try Row Count Fallback (for tiny 1s migrations)
                          let totalBytes = parseInt(selectedJob.total_bytes) || 0;
                          if (totalBytes === 0 && logs && logs.length > 0) {
                            const rowCountLogs = logs.filter(l => l.message && l.message.includes('has') && l.message.includes('rows'));
                            rowCountLogs.forEach(log => {
                              const match = log.message.match(/has\s+(\d+)\s+rows/);
                              if (match && match[1]) totalBytes += parseInt(match[1]) * 250;
                            });
                          }

                          // 3. Calculator
                          const start = new Date(selectedJob.started_at);
                          const end = selectedJob.completed_at ? new Date(selectedJob.completed_at) : new Date();
                          const durationInSeconds = Math.max(0.1, (end - start) / 1000);
                          const totalBits = totalBytes * 8;
                          const avgMbps = (totalBits / (1024 * 1024)) / durationInSeconds;

                          if (avgMbps === 0) return '0.0';
                          return avgMbps < 0.1 ? avgMbps.toFixed(4) : avgMbps.toFixed(1);
                        })()
                      ) : '0.0'}
                    </span>
                    <span className="unit"> Mbps</span>
                  </div>
                  <span className="stat-hint">
                    {selectedJob?.status === 'completed' ? "Final avg. speed" : "Avg. connection speed"}
                  </span>
                </div>
              </div>

              {/* Speed Stat */}
              <div className="mini-stat speed-card premium">
                <div className="stat-glow-bg"></div>
                <div className="mini-label-group">
                  <span className="mini-label">Current Speed</span>
                  <div className="stat-icon-halo speed">
                    <Zap size={16} className="stat-mono-icon" />
                  </div>
                </div>
                <div className="stat-main-content">
                  <div className="primary-speed">
                    <span className="mini-val highlight dashboard-font">
                      {throughputData.length > 0 ? parseFloat(throughputData[throughputData.length - 1].volume).toFixed(2) : '0.00'}
                    </span>
                    <span className="unit"> MB/s</span>
                  </div>
                  <span className="stat-hint">Live data flow</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default MonitorSection;